/**
 * usePlaces.ts — Thin React hook orchestrating place search.
 *
 * Refactored in H-203: business logic extracted to src/services/.
 * This hook owns React state, AbortController, and re-exports types.
 */

import { useState, useRef } from 'react';
import type { DistanceSettings, CategorySettings } from '@/components/SettingsModal';
import {
  type Place,
  type SearchResult,
  type ValidationResult,
  MAX_RADIUS_BY_TRANSPORT,
  EXPANSION_STEPS,
  LANGUAGE_CODE_MAP,
  MEDIUM_TOPUP_THRESHOLD,
  validateLocation,
  enrichWithTransitTimes,
} from '@/services/googlePlaces';
import {
  type FindMoreStage,
  type SearchState,
  type SearchProgressCallback,
  GRID_RADIUS_CAP_METERS,
  performInitialSearch,
  performCategoryTopup,
  performGridSearch,
  performBroaderTypeSearch,
  performRatingDropSearch,
  searchCustomVibe,
  loadMoreInternal,
  canLoadMoreInternal,
} from '@/services/placeSearch';

// Re-export types for existing consumers
export type { Place, FindMoreStage, SearchProgressCallback, SearchResult, ValidationResult };

export function usePlaces() {
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filteredClosedCount, setFilteredClosedCount] = useState(0);
  const searchStateRef = useRef<SearchState | null>(null);
  const closedPlacesRef = useRef<Place[]>([]);
  const progressCallbackRef = useRef<SearchProgressCallback | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const searchNearby = async (
    location: string,
    vibes: string[],
    coords?: { lat: number; lng: number },
    transport?: string,
    timeAvailable?: string,
    distanceSettings?: DistanceSettings,
    categorySettings?: CategorySettings,
    includeClosedPlaces: boolean = false,
    minRating: number = 3.7,
    onProgress?: SearchProgressCallback,
    language: string = 'en',
    customVibe?: string
  ): Promise<SearchResult> => {
    // Abort any in-flight search
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const { signal } = controller;

    setLoading(true);
    setError(null);
    setFilteredClosedCount(0);
    closedPlacesRef.current = [];
    progressCallbackRef.current = onProgress || null;

    // For full-day and multi-day trips, don't ask Google to filter by openNow.
    // The user has enough time to plan around opening hours, and the client-side
    // filter (splitOpenClosed) already treats these time slots as "always open".
    // Sending openNow to Google for these slots needlessly removes results.
    const longTrip = timeAvailable === 'full-day' || timeAvailable === '2-3-days';
    const effectiveIncludeClosed = includeClosedPlaces || longTrip;

    try {
      let searchCoords = coords;
      let correctedLocation: string | undefined;

      // Only validate if coords weren't already resolved by the caller (App.tsx).
      // Skipping the duplicate call saves 2-4 Google API round-trips per search.
      if (!searchCoords) {
        const validationResult = await validateLocation(location, signal);
        if (validationResult) {
          searchCoords = validationResult.coords;
          correctedLocation = validationResult.correctedName;
        } else {
          throw new Error('Could not find that location. Please check the spelling and try again.');
        }
      }

      const transportKey = (transport || 'walking') as keyof DistanceSettings;
      const timeKey = (timeAvailable || 'half-day') as keyof DistanceSettings['walking'];
      const initialRadius = distanceSettings?.[transportKey]?.[timeKey] ?? 5000;
      const maxRadius = MAX_RADIUS_BY_TRANSPORT[transportKey] || 50000;
      const radiusStep = Math.max((maxRadius - initialRadius) / EXPANSION_STEPS, 1000);
      const languageCode = LANGUAGE_CODE_MAP[language] || 'en';

      const seenPlaceIds = new Set<string>();
      const finalLocation = correctedLocation || location;

      searchStateRef.current = {
        coords: searchCoords,
        vibes,
        transport: transport || 'walking',
        timeAvailable: timeAvailable || 'half-day',
        location: finalLocation,
        language: languageCode,
        distanceSettings,
        categorySettings,
        baseRadius: initialRadius,
        currentRadius: initialRadius,
        maxRadius,
        radiusStep,
        seenPlaceIds,
        loadMoreAttempts: 0,
        minRating,
        maxDistanceMeters: initialRadius
      };

      console.log('Search params:', { location: finalLocation, vibes, coords: searchCoords, initialRadius, maxRadius, languageCode });

      const customVibeSeenIds = new Set<string>();
      const [result, customVibeResult] = await Promise.all([
        performInitialSearch(
          searchCoords,
          vibes,
          transport || 'walking',
          timeAvailable || 'half-day',
          finalLocation,
          categorySettings,
          initialRadius,
          seenPlaceIds,
          effectiveIncludeClosed,
          minRating,
          languageCode,
          initialRadius,
          signal
        ),
        customVibe
          ? searchCustomVibe(
              `${customVibe} in ${finalLocation}`,
              searchCoords, initialRadius, minRating, customVibeSeenIds,
              vibes, customVibe, finalLocation,
              transport || 'walking', timeAvailable || 'half-day',
              languageCode, maxRadius, signal
            )
          : Promise.resolve({ places: [] as Place[], closedPlaces: [] as Place[] })
      ]);

      searchStateRef.current!.currentRadius = initialRadius;

      const customVibePlaces = customVibeResult.places.filter(p => !seenPlaceIds.has(p.id));
      customVibePlaces.forEach(p => seenPlaceIds.add(p.id));
      const allPlaces = [...result.places, ...customVibePlaces];
      const allClosed = [...result.closedPlaces, ...customVibeResult.closedPlaces.filter(p => !seenPlaceIds.has(p.id))];

      // For transit mode: enrich places with estimated journey times using
      // nearby transit station data (one extra Places API call, then pure maths).
      const finalPlaces = (transport === 'transit' && searchCoords)
        ? await enrichWithTransitTimes(allPlaces, searchCoords, languageCode, signal)
        : allPlaces;

      closedPlacesRef.current = allClosed;
      setFilteredClosedCount(effectiveIncludeClosed ? 0 : allClosed.length);
      setLoading(false);

      // Flag when custom vibe text search returned nothing (API 400 or no matches)
      const customVibeDegraded = !!customVibe && customVibeResult.places.length === 0;

      return {
        places: finalPlaces,
        filteredClosedCount: effectiveIncludeClosed ? 0 : allClosed.length,
        correctedLocation,
        customVibeDegraded
      };
    } catch (err) {
      if ((err as Error).name === 'AbortError') {
        // Search was superseded by a newer search — don't set error state
        setLoading(false);
        return { places: [], filteredClosedCount: 0, aborted: true };
      }
      console.error('Place search failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to search places');
      setLoading(false);
      return { places: [], filteredClosedCount: 0, failed: true };
    }
  };

  const performCategoryTopupForRanking = async (
    rankedCount: number,
    aiMode: string = 'low'
  ): Promise<Place[]> => {
    if (!searchStateRef.current || aiMode === 'low') {
      return [];
    }

    if (aiMode === 'medium' && rankedCount >= MEDIUM_TOPUP_THRESHOLD) {
      return [];
    }

    const state = searchStateRef.current;
    progressCallbackRef.current?.('topup');

    const topupResult = await performCategoryTopup(
      state.coords,
      state.vibes,
      state.transport,
      state.timeAvailable,
      state.location,
      state.categorySettings,
      state.currentRadius,
      state.seenPlaceIds,
      false,
      state.minRating,
      state.language,
      state.maxDistanceMeters
    );

    return topupResult.places;
  };

  const getProgressCallback = (): SearchProgressCallback | null => {
    return progressCallbackRef.current;
  };

  const loadMore = async (includeClosedPlaces: boolean = false): Promise<{ places: Place[]; newMinRating: number; reachedLimit: boolean }> => {
    if (!searchStateRef.current) {
      return { places: [], newMinRating: 0, reachedLimit: true };
    }

    setLoadingMore(true);
    try {
      const result = await loadMoreInternal(searchStateRef.current, includeClosedPlaces);
      setLoadingMore(false);
      return result;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load more places');
      setLoadingMore(false);
      return { places: [], newMinRating: searchStateRef.current?.minRating ?? 0, reachedLimit: true };
    }
  };

  const loadMoreByStage = async (stage: FindMoreStage): Promise<{ places: Place[]; reachedLimit: boolean; effectiveStage: FindMoreStage }> => {
    if (!searchStateRef.current) {
      return { places: [], reachedLimit: true, effectiveStage: stage };
    }

    setLoadingMore(true);
    const state = searchStateRef.current;

    try {
      let places: Place[] = [];
      let effectiveStage: FindMoreStage = stage;

      // Tier 1 may need to fall through to Tier 2 when the grid is skipped
      // (R > 20km) or returns empty. We make that explicit by invoking the
      // Tier 2 logic from inside Tier 1, then bumping `stage` so the switch
      // moves on. This avoids switch fall-through (and its TS7029 / ESLint
      // no-fallthrough warnings) while keeping the tier order semantics.
      const runTier2 = async (): Promise<Place[]> => {
        const broaderResult = await performBroaderTypeSearch(state);
        return broaderResult.places;
      };

      switch (stage) {
        // ── Tier 1: Grid search (3 API calls) ──────────────────────────
        case 1: {
          const skipGrid = state.baseRadius > GRID_RADIUS_CAP_METERS;
          if (!skipGrid) {
            const gridResult = await performGridSearch(
              state.coords, state.vibes, state.transport,
              state.timeAvailable, state.categorySettings,
              state.baseRadius, state.seenPlaceIds,
              false, state.minRating, state.language
            );
            places = gridResult.places;
            if (places.length > 0) break;
          }
          // Grid skipped (R > 20km) or empty → run Tier 2 inline.
          places = [...places, ...(await runTier2())];
          effectiveStage = 2;
          break;
        }
        // ── Tier 2: Type broadening (1 API call) ───────────────────────
        case 2: {
          places = [...places, ...(await runTier2())];
          break;
        }

        // ── Tier 3: Rating drop to 0 (1 API call) ─────────────────────
        case 3: {
          state.minRating = 0;
          const ratingResult = await performRatingDropSearch(state);
          places = ratingResult.places;
          break;
        }

        // ── Tier 4: Include closed places (0 API calls) ────────────────
        case 4: {
          const cached = closedPlacesRef.current;
          const unseen = cached.filter(p => !state.seenPlaceIds.has(p.id));
          unseen.forEach(p => state.seenPlaceIds.add(p.id));
          places = unseen;
          break;
        }

        // ── Tier 5: Exhausted ──────────────────────────────────────────
        case 5:
          setLoadingMore(false);
          return { places: [], reachedLimit: true, effectiveStage: 5 };

        default:
          break;
      }

      // Enrich transit places
      const enrichedPlaces = (state.transport === 'transit' && places.length > 0)
        ? await enrichWithTransitTimes(places, state.coords, state.language)
        : places;

      setLoadingMore(false);
      return { places: enrichedPlaces, reachedLimit: effectiveStage >= 5, effectiveStage };
    } catch (err) {
      if ((err as Error).name === 'AbortError') {
        setLoadingMore(false);
        return { places: [], reachedLimit: false, effectiveStage: stage };
      }
      setError(err instanceof Error ? err.message : 'Failed to load more places');
      setLoadingMore(false);
      return { places: [], reachedLimit: true, effectiveStage: stage };
    }
  };

  const canLoadMore = (): boolean => canLoadMoreInternal(searchStateRef.current);

  const getClosedPlaces = (): Place[] => closedPlacesRef.current;

  return { searchNearby, validateLocation, loadMore, loadMoreByStage, canLoadMore, getClosedPlaces, performCategoryTopupForRanking, getProgressCallback, loading, loadingMore, error, filteredClosedCount };
}
